/**
 * A tab opened before a deploy keeps running the old app against the new API
 * until it is reloaded: on 2026-10-08 a refused video's page showed the new
 * message without the new rows that go with it, because the tab still ran the
 * build from the day before. Moving between pages in the app loads no code,
 * so nothing replaced it.
 *
 * The app knows which bundle it runs (its `<script src=/assets/index-*.js>`);
 * the page at `/` names the bundle now deployed (index.html is served with
 * max-age=0). When they differ the app is stale, and the next page change
 * becomes a full load of the page asked for: nothing on screen is lost, and
 * the user lands on the page they clicked with the new code. Never while an
 * upload runs in this tab (the upload lives in the page), and never on the
 * dev server, whose index.html names no bundle.
 */
import { uploadInProgress } from "./upload";

const BUNDLE = /\/assets\/index-[\w-]+\.js/;
const MIN_GAP_MS = 30_000;     // at most one check of `/` per 30 s
const POLL_MS = 5 * 60_000;    // and one every 5 minutes while the tab is open

/** The bundle this tab runs; null on the dev server. */
export function runningBundle(doc: Document = document): string | null {
  for (const s of Array.from(doc.querySelectorAll("script[src]"))) {
    const m = BUNDLE.exec(s.getAttribute("src") ?? "");
    if (m) return m[0];
  }
  return null;
}

/** The bundle an index.html names; null when it names none. */
export function deployedBundle(html: string): string | null {
  return BUNDLE.exec(html)?.[0] ?? null;
}

let stale = false;
let lastCheck = 0;
let inFlight: Promise<boolean> | null = null;

/** Whether a newer build is deployed than the one this tab runs (fetches `/` at most every MIN_GAP_MS). */
export function checkForNewBuild(now = Date.now()): Promise<boolean> {
  const running = runningBundle();
  if (!running || stale) return Promise.resolve(stale);
  if (inFlight) return inFlight;
  if (now - lastCheck < MIN_GAP_MS) return Promise.resolve(false);
  lastCheck = now;
  inFlight = fetch("/", { cache: "no-store", credentials: "same-origin" })
    .then((r) => (r.ok ? r.text() : ""))
    .then((html) => {
      const deployed = deployedBundle(html);
      stale = deployed !== null && deployed !== running;
      return stale;
    })
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Reload the page now if a newer build is known and nothing in the tab would be lost. */
export function reloadIfStale(): boolean {
  if (!stale || uploadInProgress()) return false;
  window.location.reload();
  return true;
}

/** Background checks: every POLL_MS, and when the tab comes back into view. Returns the cleanup. */
export function watchForNewBuild(): () => void {
  const onVisible = () => {
    if (document.visibilityState === "visible") void checkForNewBuild();
  };
  document.addEventListener("visibilitychange", onVisible);
  const id = window.setInterval(() => void checkForNewBuild(), POLL_MS);
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.clearInterval(id);
  };
}
